import { useTranslation } from 'react-i18next';
import PageBanner from '@/components/ui/Banner/PageBanner';
import BannerContent from '@/components/ui/Banner/BannerContent';
import SectionWrapper from '@/components/privacy/SectionWrapper';
import SectionHeader from '@/components/privacy/SectionHeader';
import InfoList from '@/components/privacy/InfoList';
// import ActionButton from '@/components/privacy/ActionButton';

import Seo from '@/components/headers/seo';

const PrivacyPage = () => {
  const { t } = useTranslation('common:privacy');

  return (
    <>
      <Seo title={t('title')} />

      <div className='relative min-h-screen overflow-hidden bg-black pb-20 text-white'>
        <PageBanner
          backgroundImage='/images/privacy/privacy_bg.png'
          backgroundVideo='/images/faq/faq_bg_video.mp4'
          videoOpacity={40}
          alt='Privacy page background'
        />

        <div className='relative z-10 mx-auto px-6 pt-32'>
          <BannerContent
            icon='/icons/Shield-check.svg'
            iconAlt='Shield check icon'
            title={t('privacy.title')}
            description={t('privacy.description')}
            className='mb-[15vw] mt-[90vw] md:mt-[15vw]'
            titleClassName='text-5xl md:text-6xl lg:text-8xl'
            descriptionClassName=' lg:text-2xl lg:max-w-4xl'
          />

          {/* Introduction*/}
          <div className='mx-auto max-w-6xl space-y-6'>
            <SectionWrapper>
              <h1 className='mb-4 text-xl font-bold'>{t('privacy.introduction.title')}</h1>
              <p className='mb-6 w-full text-base leading-relaxed text-[var(--Greys-400,#A1A1AA)] md:w-[80%]'>
                {t('privacy.introduction.description')}
              </p>
              <div className='w-full rounded-2xl border border-[var(--Brand-Accent-Tertiary,#32C200)] bg-[rgba(255,255,255,0.10)] px-4 py-2'>
                <span className='text-sm text-[var(--Brand-Accent-Tertiary,#32C200)]'>
                  {t('privacy.introduction.lastUpdated')}
                </span>
                <span className='mx-1 text-sm text-white'>
                  {t('privacy.introduction.updatedDate')}
                </span>
              </div>
            </SectionWrapper>

            {/* Information We Collect Section - Main Card */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/File.svg'
                iconAlt='File icon'
                title={t('privacy.informationWeCollect.title')}
              />
              <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3'>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                  <h3 className='mb-4 text-xl font-semibold'>
                    {t('privacy.informationWeCollect.personalInfo.title')}
                  </h3>
                  <InfoList
                    items={[
                      t('privacy.informationWeCollect.personalInfo.items.item-1'),
                      t('privacy.informationWeCollect.personalInfo.items.item-2'),
                      t('privacy.informationWeCollect.personalInfo.items.item-3'),
                      t('privacy.informationWeCollect.personalInfo.items.item-4'),
                    ]}
                  />
                </div>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                  <h3 className='mb-4 text-xl font-semibold'>
                    {t('privacy.informationWeCollect.usageData.title')}
                  </h3>
                  <InfoList
                    items={[
                      t('privacy.informationWeCollect.usageData.items.item-1'),
                      t('privacy.informationWeCollect.usageData.items.item-2'),
                      t('privacy.informationWeCollect.usageData.items.item-3'),
                      t('privacy.informationWeCollect.usageData.items.item-4'),
                    ]}
                  />
                </div>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                  <h3 className='mb-4 text-xl font-semibold'>
                    {t('privacy.informationWeCollect.eventData.title')}
                  </h3>
                  <InfoList
                    items={[
                      t('privacy.informationWeCollect.eventData.items.item-1'),
                      t('privacy.informationWeCollect.eventData.items.item-2'),
                      t('privacy.informationWeCollect.eventData.items.item-3'),
                      t('privacy.informationWeCollect.eventData.items.item-4'),
                    ]}
                  />
                </div>
              </div>
            </SectionWrapper>

            {/* How We Use Your Information */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Shield-user.svg'
                iconAlt='Shield user icon'
                title={t('privacy.howWeUse.title')}
              />
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2'>
                {['1', '2', '3', '4'].map((item) => (
                  <div key={`item-howtouse-${item}`} className='p-6'>
                    <div className='flex items-start gap-4'>
                      <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-0 bg-[var(--Brand-Primary-Primary,#3885FF)]'>
                        <img
                          src={`/icons/how-to-use-${item}.svg`}
                          alt={`how to use ${item} icon`}
                          className='relative mx-auto size-[4vw] md:size-[2.2vw] lg:size-[1.1vw]'
                        />
                      </div>
                      <div>
                        <h3 className='mb-2 text-xl font-semibold'>
                          {t(`privacy.howWeUse.items.${item}.title`)}
                        </h3>
                        <p className='text-base text-[var(--Greys-400,#A1A1AA)]'>
                          {t(`privacy.howWeUse.items.${item}.description`)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionWrapper>

            {/* Sharing of Information */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Attachment.svg'
                iconAlt='Attachment icon'
                title={t('privacy.sharing.title')}
              />
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                <div className='grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2'>
                  <div>
                    <h3 className='mb-4 text-xl font-semibold'>
                      {t('privacy.sharing.weShareWith.title')}
                    </h3>
                    <InfoList
                      items={['1', '2', '3', '4'].map((item) => {
                        return t(`privacy.sharing.weShareWith.items.${item}`);
                      })}
                      variant='checkmark'
                    />
                  </div>
                  <div>
                    <h3 className='mb-4 text-xl font-semibold'>
                      {t('privacy.sharing.weNever.title')}
                    </h3>
                    <InfoList
                      items={['1', '2', '3'].map((item) => {
                        return t(`privacy.sharing.weNever.items.${item}`);
                      })}
                      variant='cross'
                    />
                  </div>
                </div>
              </div>
            </SectionWrapper>

            {/* Cookies & Tracking */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Cookie.svg'
                iconAlt='Cookie icon'
                title={t('privacy.cookies.title')}
                titleTag='h1'
              />
              <p className='mb-6 w-full leading-relaxed text-gray-300 md:w-[70%]'>
                {t('privacy.cookies.description')}
              </p>
              {/* <div className='flex flex-wrap gap-3'>
                <ActionButton className='mt-10'>{t('privacy.cookies.primaryButton')}</ActionButton>
                <span className='mx-1 hidden md:block'></span>
                <ActionButton variant='secondary' className='mt-5 lg:mt-10'>
                  {t('privacy.cookies.secButton')}
                </ActionButton>
              </div> */}
            </SectionWrapper>

            {/* Data Protection & Security */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Lock-circle.svg'
                iconAlt='Lock icon'
                title={t('privacy.DataProtection.title')}
              />
              <div className='mt-6 grid gap-6 md:grid-cols-3'>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6 text-center'>
                  <div className='mx-auto mb-3 h-8 w-8'>
                    <img src='/icons/privacy/shield.svg' alt='Data protection icon' />
                  </div>
                  <h3 className='mb-2 text-xl font-semibold text-white'>
                    {t('privacy.DataProtection.cardOne.title')}
                  </h3>
                  <p className='text-[var(--Greys-400,#A1A1AA)]'>
                    {t('privacy.DataProtection.cardOne.subTitle')}
                  </p>
                </div>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6 text-center'>
                  <div className='mx-auto mb-3 h-8 w-8'>
                    <img src='/icons/privacy/encryption.svg' alt='Data protection icon' />
                  </div>
                  <h3 className='mb-2 text-xl font-semibold text-white'>
                    {t('privacy.DataProtection.cardTwo.title')}
                  </h3>
                  <p className='text-[var(--Greys-400,#A1A1AA)]'>
                    {t('privacy.DataProtection.cardTwo.subTitle')}
                  </p>
                </div>
                <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6 text-center'>
                  <div className='mx-auto mb-3 h-8 w-8'>
                    <img src='/icons/privacy/eye.svg' alt='Data protection icon' />
                  </div>
                  <h3 className='mb-2 text-xl font-semibold text-white'>
                    {t('privacy.DataProtection.cardThree.title')}
                  </h3>
                  <p className='text-[var(--Greys-400,#A1A1AA)]'>
                    {t('privacy.DataProtection.cardThree.subTitle')}
                  </p>
                </div>
              </div>
            </SectionWrapper>

            {/* Your Rights */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Add-user.svg'
                iconAlt='Add user icon'
                title={t('privacy.youRights.title')}
              />
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                <div className='mb-6 mt-2 grid gap-8 md:grid-cols-2'>
                  <div>
                    <h3 className='mb-4 text-xl font-semibold text-white'>
                      {t('privacy.youRights.youHaveRight.title')}
                    </h3>
                    <InfoList
                      items={['1', '2', '3', '4', '5'].map((item) =>
                        t(`privacy.youRights.youHaveRight.items.${item}`)
                      )}
                    />
                  </div>
                  <div className='mr-10'>
                    <h3 className='mb-4 text-xl font-semibold text-white'>
                      {t('privacy.youRights.howToEcercies.title')}
                    </h3>
                    <p className='mb-4 text-[var(--Greys-400,#A1A1AA)]'>
                      {t('privacy.youRights.howToEcercies.description')}
                    </p>
                    {/* <ActionButton className='mt-10'>
                      {t('privacy.youRights.howToEcercies.primaryButton')}
                    </ActionButton> */}
                  </div>
                </div>
              </div>
            </SectionWrapper>

            {/* Children's Privacy */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Chair.svg'
                iconAlt='Chair icon'
                title={t('privacy.childernPrivacy.title')}
                titleTag='h1'
              />
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-8'>
                <p className='mb-6 w-full leading-relaxed text-[var(--Greys-400,#A1A1AA)] md:w-[80%]'>
                  {t('privacy.childernPrivacy.description')}
                </p>
                <p className='text-[var(--Greys-400,#A1A1AA)]'>
                  {t('privacy.childernPrivacy.secDescription')}
                </p>
              </div>
            </SectionWrapper>

            {/* Third-Party Links */}
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Clip.svg'
                iconAlt='Clip icon'
                title={t('privacy.thirdParty.title')}
                titleTag='h1'
              />
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                <p className='w-full leading-relaxed text-[var(--Greys-400,#A1A1AA)] md:w-[85%]'>
                  <span className='mr-1 font-semibold'>
                    {t('privacy.thirdParty.boldDescTitle')}
                  </span>
                  {t('privacy.thirdParty.description')}
                </p>
              </div>
            </SectionWrapper>

            {/* Changes to This Policy */}
            <SectionWrapper>
              <div className='mb-6 flex items-center gap-3'>
                <div className='relative size-[32px]'>
                  <img
                    src='/icons/privacy/shield-star.svg'
                    alt='shield star icon'
                    className='absolute size-full'
                  />
                </div>
                <h1 className='text-xl font-bold'>{t('privacy.chagneToThis.title')}</h1>
              </div>
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6'>
                <p className='mb-6 text-[var(--Greys-400,#A1A1AA)]'>
                  {t('privacy.chagneToThis.description')}
                </p>
                <InfoList
                  items={['1', '2', '3', '4'].map((item) =>
                    t(`privacy.chagneToThis.items.${item}`)
                  )}
                  className='mb-6'
                />
                <p className='text-[var(--Greys-400,#A1A1AA)]'>
                  {t('privacy.chagneToThis.subDesc')}
                </p>
              </div>
            </SectionWrapper>

            {/* Contact Information */}
            {/* 
            <SectionWrapper>
              <SectionHeader
                iconSrc='/icons/Clipboard-check.svg'
                iconAlt='Clipboard icon'
                title={t('privacy.contactInfo.title')}
              />
              <div className='rounded-2xl border-0 bg-[rgba(255,255,255,0.03)] p-6 pr-10'>
                <p className='mb-8 text-sm text-[var(--Greys-400,#A1A1AA)]'>
                  {t('privacy.contactInfo.description')}
                </p>
                <div className='grid gap-8 md:grid-cols-2'>
                  <div>
                    <h3 className='mb-4 text-lg font-semibold text-white'>
                      {t('privacy.contactInfo.privacyOfficer.title')}
                    </h3>
                    <div className='space-y-3'>
                      <div className='flex items-center text-sm text-[var(--Greys-400,#A1A1AA)]'>
                        <span className='mr-3 flex h-4 w-4 items-center justify-center'>
                          <img src='/icons/Mail.svg' alt='Mail icon' className='h-4 w-4' />
                        </span>
                        {t('privacy.contactInfo.privacyOfficer.mail')}
                      </div>
                      <div className='flex items-center text-sm text-[var(--Greys-400,#A1A1AA)]'>
                        <span className='mr-3 flex h-4 w-4 items-center justify-center'>
                          <img src='/icons/Call.svg' alt='phone icon' className='h-4 w-4' />
                        </span>
                        <span className='rtl:[direction:ltr]'>
                          {t('privacy.contactInfo.privacyOfficer.phone')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className='mb-4 text-xl font-semibold text-white'>
                      {t('privacy.contactInfo.responseTime.title')}
                    </h3>
                    <p className='mb-4 me-10 text-[var(--Greys-400,#A1A1AA)]'>
                      {t('privacy.contactInfo.responseTime.desc')}
                    </p>
                   <ActionButton>
                      {t('privacy.contactInfo.responseTime.primaryButton')}
                    </ActionButton> 
                  </div>
                </div>
              </div>
            </SectionWrapper>
             */}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;
